import type { FaqItem } from "@marketing/components/blocks";

/**
 * One dated, sourced fact sheet per vendor, read by /compare/<slug>, the /compare hub and
 * /best-ai-shopping-assistants. Every value comes from the vendor's own pages (or launch coverage,
 * where noted) on the `checked` date. When a page does not say, the value is "Not stated
 * publicly": that never means the vendor lacks it. Hephon Commerce's column comes only from our
 * own published facts. Re-check a vendor before changing its row, and move `checked` with it.
 */

export const CHECKED = "2026-11-05";

/** The rows of every comparison table, in order. */
export const ATTRIBUTES = [
	{ key: "channels", label: "Channels" },
	{ key: "languages", label: "Languages" },
	{ key: "arabic", label: "Arabic dialects" },
	{ key: "arabizi", label: "Arabizi (Arabic in Latin letters)" },
	{ key: "voice", label: "Voice and phone ordering" },
	{ key: "whatsapp", label: "WhatsApp ordering and checkout" },
	{ key: "catalog", label: "Catalog search, branch prices and stock" },
	{ key: "recipes", label: "Recipes to basket" },
	{ key: "checkout", label: "Checkout and shopper confirmation" },
	{ key: "contentRules", label: "Halal and content rules" },
	{ key: "handover", label: "Handover to people" },
	{ key: "integrations", label: "Integrations" },
	{ key: "pricing", label: "Pricing" },
	{ key: "data", label: "Hosting, data and certifications" },
	{ key: "market", label: "Primary market" },
] as const;

export type AttributeKey = (typeof ATTRIBUTES)[number]["key"];

export interface Fact {
	value: string;
	/** The page the value was read from (or, for "Not stated publicly", the page checked). */
	source: string;
}

export type VendorGroup = "global" | "gulf" | "other";

export interface Vendor {
	slug: string;
	name: string;
	/** How the name reads after "Hephon Commerce vs", when it differs from `name`. */
	vsName?: string;
	url: string;
	group: VendorGroup;
	/** What it is, in one line. */
	summary: string;
	bestFor: string;
	/** Meta description of its comparison page. */
	description: string;
	/** Who should choose which, in three sentences. */
	inShort: string;
	chooseThem: string[];
	chooseUs: string[];
	/** Where the vendor is stronger than Hephon Commerce, each point true on its own. */
	stronger: string[];
	hephonStronger: string[];
	/** Honest limits, for the "best of" list. */
	limits: string[];
	/** How a retailer can run Hephon Commerce next to it without switching. */
	alongside: string;
	faq: FaqItem[];
	facts: Record<AttributeKey, Fact>;
	checked: string;
}

const NS = "Not stated publicly.";

/** Hephon Commerce's own column: only facts we publish. */
export const HEPHON = {
	name: "Hephon Commerce",
	bestFor:
		"Supermarkets, grocery delivery and quick-commerce retailers in Saudi Arabia and the UAE that want shoppers to order in Gulf Arabic, Arabizi or English inside their app, on WhatsApp and by phone.",
	strengths: [
		"Shopping is the whole product: it searches the live catalog with each branch's prices and stock, checks every result against what the shopper asked, and fills the basket.",
		"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with a phrasebook of approved replies per dialect that the retailer can edit.",
		"Each branch's own prices and stock, in the retailer's currency.",
		"Recipes become baskets: a dish and a number of people turn into amounts, the cheapest pack that covers each line, and one tap to add all.",
		"Phone and WhatsApp call ordering, with the basket read back and keypad 1 to confirm.",
		"Alcohol, pork and tobacco can be hidden by the server before the assistant sees the catalog, on every channel.",
	],
	limits: [
		"Access is by invitation, through a demo. There is no self-serve sign-up.",
		"No public price: each retailer gets a proposal based on stores and channels.",
	],
	facts: {
		channels: {
			value: "Inside the retailer's iOS, Android and web apps (SDK, also React Native and Flutter) and website, its WhatsApp Business number, phone calls, and a hosted chat page.",
			source: "/channels/in-app",
		},
		languages: {
			value: "Arabic, English and French, and mixes of them, answered in the language and script of the question. By voice, more than 30 languages.",
			source: "/arabic-chatbot-for-ecommerce",
		},
		arabic: {
			value: "Saudi and Gulf Arabic as people write and speak them, and Lebanese Arabic. Replies follow a phrasebook of approved phrases per dialect.",
			source: "/saudi-arabia",
		},
		arabizi: {
			value: 'Yes, for example "7aleeb" for milk, answered in the same script.',
			source: "/arabic-chatbot-for-ecommerce",
		},
		voice: {
			value: "Phone and WhatsApp calls: it builds the basket during the call, reads it back, and keypad 1 confirms. Replies in under a second; a caller can be handed to a person.",
			source: "/channels/voice",
		},
		whatsapp: {
			value: "On the retailer's own number: product cards, basket, and a checkout the shopper confirms with a tap. Voice notes and calls too.",
			source: "/channels/whatsapp",
		},
		catalog: {
			value: "Keyword and meaning search over the live catalog, each result checked against the shopper's message before it is shown. Each branch's own prices and stock.",
			source: "/how-it-works",
		},
		recipes: {
			value: 'Yes. "Kabsa for 10" becomes ingredient amounts, the cheapest pack that covers each line and how many, and "add all".',
			source: "/shoppable-recipes",
		},
		checkout: {
			value: "Nothing is ordered without the shopper's explicit confirmation: a tap, a signed checkout page, or keypad 1 after the read-back. Orders go to the retailer's platform or team.",
			source: "/how-it-works",
		},
		contentRules: {
			value: "Alcohol, pork and tobacco can be hidden by the server before the assistant sees the catalog; products not yet checked stay hidden. On WhatsApp, alcohol and tobacco are always excluded.",
			source: "/security",
		},
		handover: {
			value: "One live inbox for chats, voice notes and calls with transcripts; a person can take over any chat. Handover to your help desk by integration.",
			source: "/how-it-works",
		},
		integrations: {
			value: "Salla, Zid, Shopify, WooCommerce, product feeds, a REST API with signed webhooks, and custom connectors.",
			source: "/integrations",
		},
		pricing: {
			value: "A proposal per retailer, based on stores and channels. No public price; access by invitation.",
			source: "/demo",
		},
		data: {
			value: "Servers in the European Union today. No conversation is used to train an AI model. No ISO or SOC certification.",
			source: "/security",
		},
		market: {
			value: "Saudi Arabia and the UAE first, and Lebanon.",
			source: "/",
		},
	} satisfies Record<AttributeKey, Fact>,
};

export const VENDORS: Vendor[] = [
	{
		slug: "yellow-ai",
		name: "Yellow.ai",
		url: "https://yellow.ai",
		group: "gulf",
		summary:
			"An enterprise agentic AI platform for customer service, with chat, voice and email agents across 35+ channels. It built Lulu Hypermarket's WhatsApp assistant.",
		bestFor:
			"Large enterprises that want one AI platform for customer service, loyalty and marketing messages across many countries and channels.",
		description:
			"Hephon Commerce vs Yellow.ai for grocery retailers: Gulf Arabic, WhatsApp ordering, branch prices, voice and pricing compared, with a source for every row.",
		inShort:
			"Choose Yellow.ai if you want one enterprise platform for customer service, loyalty and marketing messages, with published security certifications and 135+ languages. Choose Hephon Commerce if the job is shoppers buying from your grocery catalog in Gulf Arabic, with each branch's prices, inside your app, on WhatsApp and by phone. Many retailers could run both, split by channel.",
		chooseThem: [
			"You need one vendor for service, loyalty, receipts and campaigns.",
			"Your security review requires ISO 27001 or SOC 2 Type II.",
			"You operate in many countries and languages.",
			"You want to start on a free plan.",
		],
		chooseUs: [
			"Shoppers should build and order a grocery basket in the chat or on a call.",
			"You need each branch's prices and stock.",
			"Saudi and Gulf Arabic and Arabizi matter more than language count.",
			"Halal rules must be enforced on every channel.",
		],
		stronger: [
			"A live Gulf grocery reference: Lulu Hypermarket's WhatsApp assistant handles order tracking, receipts, loyalty points, offers and in-store pickup orders.",
			"Breadth: 35+ voice and text channels, 135+ languages, email automation and employee-experience agents.",
			"Published certifications: ISO 27001, ISO 27701, SOC 2 Type II, PCI-DSS v4.0.1 and HIPAA.",
			"A free plan (500 chat sessions a month) you can try today.",
			"Help-desk connections named on its site, including Zendesk, Freshdesk, HubSpot and Genesys.",
		],
		hephonStronger: [
			"Shopping is the whole product: search checked against the shopper's message, budgets, cheapest first and similar items.",
			"Each branch's own prices and stock.",
			"Recipes to basket with pack sizes.",
			"Phone ordering with the basket read back and keypad 1 to confirm.",
			"Content rules enforced by the server, not by the prompt.",
		],
		limits: [
			"Arabic dialects and Arabizi support are not stated publicly.",
			"Grocery basket building, branch prices and recipes are not described on its retail pages.",
			"Enterprise pricing is quote-based.",
		],
		alongside:
			"Keep Yellow.ai on your service and loyalty journeys and add Hephon Commerce inside your app for shopping first. We work out WhatsApp and phone with your team.",
		faq: [
			{
				q: "Is Yellow.ai a shopping assistant or a customer service platform?",
				a: "Its site describes an agentic AI platform for customer and employee experience. Its retail page covers product recommendations, availability checks and refunds. Hephon Commerce does one job: shoppers search, build a basket and order from your catalog.",
			},
			{
				q: "Which one handles Gulf Arabic better?",
				a: "Yellow.ai supports Arabic (Lulu's assistant runs in English and Arabic) and states 135+ languages, but its pages we checked do not name dialects or Arabizi. Hephon Commerce is built around Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with an editable phrasebook per dialect.",
			},
			{
				q: "How do the prices compare?",
				a: "Yellow.ai has a free plan with 500 chat sessions a month, then $0.99 per resolution, and quote-based Enterprise pricing. Hephon Commerce has no public price: each retailer gets a proposal based on stores and channels.",
			},
			{
				q: "Which has more security certifications?",
				a: "Yellow.ai. It lists ISO 27001, ISO 27701, SOC 2 Type II, PCI-DSS and HIPAA. Hephon Commerce has no ISO or SOC certification; our servers are in the European Union and no conversation is used to train a model.",
			},
		],
		facts: {
			channels: {
				value: "35+ voice and text channels on the Enterprise plan, including WhatsApp, web chat, voice and email.",
				source: "https://yellow.ai/pricing/",
			},
			languages: {
				value: "Native support for 135+ languages; its Nexus VOX voice agents state 500+.",
				source: "https://yellow.ai/campaign/yellow-ai-vs-competitors",
			},
			arabic: {
				value: "Arabic is supported: Lulu's assistant runs in English and Arabic. Which dialects is not stated publicly.",
				source: "https://yellow.ai/case-study/lulu-hypermarket-ex-automation/",
			},
			arabizi: {
				value: NS,
				source: "https://yellow.ai/campaign/yellow-ai-vs-competitors",
			},
			voice: {
				value: "Voice agents for inbound queries and outbound calls. Grocery phone ordering is not stated publicly.",
				source: "https://yellow.ai/customer-experience-platform/retail-customer-experience/",
			},
			whatsapp: {
				value: "For Lulu Hypermarket: order tracking, digital receipts, loyalty points, offers and in-store pickup ordering on WhatsApp. A delivery-basket checkout is not stated publicly.",
				source: "https://yellow.ai/case-study/lulu-hypermarket-ex-automation/",
			},
			catalog: {
				value: "Product recommendations from your customer data platform, and product availability checks. Branch prices and stock are not stated publicly.",
				source: "https://yellow.ai/customer-experience-platform/retail-customer-experience/",
			},
			recipes: {
				value: NS,
				source: "https://yellow.ai/customer-experience-platform/retail-customer-experience/",
			},
			checkout: {
				value: NS,
				source: "https://yellow.ai/customer-experience-platform/retail-customer-experience/",
			},
			contentRules: {
				value: NS,
				source: "https://yellow.ai/customer-experience-platform/retail-customer-experience/",
			},
			handover: {
				value: "Complex cases go to human agents with full context (Lulu case study).",
				source: "https://yellow.ai/case-study/lulu-hypermarket-ex-automation/",
			},
			integrations: {
				value: "Zendesk, Freshdesk, HubSpot and Genesys are named, with an integrations directory. Shopify, Salla or Zid are not stated publicly.",
				source: "https://yellow.ai/campaign/yellow-ai-vs-competitors",
			},
			pricing: {
				value: "Free plan with 500 chat sessions a month, then $0.99 per resolution. Enterprise is quote-based.",
				source: "https://yellow.ai/pricing/",
			},
			data: {
				value: "Lists ISO 27001, ISO 27701, SOC 2 Type II, PCI-DSS v4.0.1 and HIPAA. Hosting regions are not stated publicly.",
				source: "https://yellow.ai/",
			},
			market: {
				value: "Global enterprises. Gulf grocery reference: Lulu Hypermarket on WhatsApp.",
				source: "https://yellow.ai/case-study/lulu-hypermarket-ex-automation/",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "nateq",
		name: "Nateq",
		url: "https://nateq.io",
		group: "gulf",
		summary:
			"A Gulf customer service platform: one inbox for WhatsApp, Instagram, Facebook, web chat, email and calls, with AI agents that answer in Arabic dialects.",
		bestFor:
			"Gulf businesses that want an Arabic-first support inbox and AI agent across many channels, with public per-seat prices and a free trial.",
		description:
			"Hephon Commerce vs Nateq for Gulf retailers: Arabic dialects, WhatsApp ordering, Salla and Zid, voice, branch prices and pricing, compared with sources.",
		inShort:
			"Choose Nateq if you want an Arabic-first customer service inbox across WhatsApp, Instagram, Facebook, email and calls, with Salla and Zid connectors and public prices. Choose Hephon Commerce if shoppers should search your grocery catalog, build a basket with each branch's prices and confirm an order, in your app, on WhatsApp or by phone. Nateq answers questions about your store; Hephon Commerce shops it with the customer.",
		chooseThem: [
			"You need a shared support inbox with seats, SLAs and overflow teams.",
			"Instagram, Facebook and email matter as much as WhatsApp.",
			"You want to start this week on a free trial.",
		],
		chooseUs: [
			"Shoppers should order a full grocery basket in the chat or on a call.",
			"Prices and stock differ by branch.",
			"You want recipes, budgets and pack sizes handled for the shopper.",
			"The assistant should live inside your own app.",
		],
		stronger: [
			"Public prices: from $15 a seat a month, AI resolutions from $0.09 to $0.20, voice from $0.10 a minute, and a 14-day free trial.",
			"Salla and Zid catalog connectors, plus Shopify, CSV, product feeds and Meta Commerce.",
			"More dialects named: Gulf, Egyptian, Levantine and Maghrebi Arabic, plus Franco-Arabic.",
			"A full support inbox across WhatsApp, Instagram, Facebook, web chat, email and calls, with overflow teams and keyword routing to staff.",
			"Limits you set on refunds and discounts the agent may commit.",
		],
		hephonStronger: [
			"Builds the basket and the checkout, with an explicit shopper confirmation before any order.",
			"Each branch's own prices and stock.",
			"Recipes to basket with pack sizes, budgets and cheapest-first.",
			"Phone ordering with read-back and keypad confirmation.",
			"An SDK for your iOS, Android and web apps.",
			"Content rules enforced by the server.",
		],
		limits: [
			"Its ecommerce page does not describe the agent placing orders; it sends payment links.",
			"Per-branch prices and stock are not stated publicly.",
			"A young company (founded 2025, registered in Wyoming, USA).",
		],
		alongside:
			"Keep Nateq as your support inbox and run Hephon Commerce inside your app for shopping, or on a second WhatsApp number for orders.",
		faq: [
			{
				q: "Can Nateq take a grocery order on WhatsApp?",
				a: "Its ecommerce page says the agent answers product questions from your synced catalog, looks up orders and sends payment links from Moyasar, Tap or Stripe. It does not describe the agent placing orders. Hephon Commerce builds the basket and a checkout the shopper confirms with a tap.",
			},
			{
				q: "Which supports more Arabic dialects?",
				a: "Nateq names Gulf, Egyptian, Levantine and Maghrebi Arabic and Franco-Arabic. Hephon Commerce focuses on Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with an editable phrasebook of approved replies per dialect.",
			},
			{
				q: "Does either connect to Salla or Zid?",
				a: "Yes, both. Nateq says Shopify, Salla and Zid stores connect from its app. Hephon Commerce connects to Salla and Zid directly, with stock per branch, as well as Shopify, WooCommerce, product feeds, its REST API and custom connectors.",
			},
			{
				q: "Which is cheaper?",
				a: "Nateq publishes per-seat prices from $15 a month plus usage for AI resolutions and voice. Hephon Commerce has no public price; you get a proposal based on stores and channels.",
			},
		],
		facts: {
			channels: {
				value: "WhatsApp, Instagram, Facebook, website chat, email and calls, in one inbox.",
				source: "https://nateq.io/llms.txt",
			},
			languages: {
				value: "Arabic and English.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			arabic: {
				value: "Gulf, Egyptian, Levantine and Maghrebi Arabic, and mixed Arabic-English.",
				source: "https://nateq.io/arabic-voice-ai",
			},
			arabizi: {
				value: "Franco-Arabic (Arabic in Latin letters) is named.",
				source: "https://nateq.io/arabic-chatbot",
			},
			voice: {
				value: "Arabic voice AI answers calls and WhatsApp voice notes in the caller's dialect. Phone ordering is not stated publicly.",
				source: "https://nateq.io/arabic-voice-ai",
			},
			whatsapp: {
				value: "Native WhatsApp product messages through Meta Commerce, and payment links in the chat. Placing orders is not described.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			catalog: {
				value: "Products and availability sync from Shopify, Salla or Zid, or from a CSV or feed; answers product questions and recommends. Per-branch prices and stock are not stated publicly.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			recipes: {
				value: NS,
				source: "https://nateq.io/solutions/ecommerce",
			},
			checkout: {
				value: "Payment links from Moyasar, Tap or Stripe; the customer pays on the provider's page.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			contentRules: {
				value: `${NS} You can cap the refunds and discounts the agent commits.`,
				source: "https://nateq.io/solutions/ecommerce",
			},
			handover: {
				value: "Hands over to a person with the full thread; keyword routing to staff; overflow team on peak days.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			integrations: {
				value: "Shopify, Salla and Zid catalogs, CSV and product feeds, Meta Commerce, and Moyasar, Tap or Stripe payment links.",
				source: "https://nateq.io/solutions/ecommerce",
			},
			pricing: {
				value: "$15 to $75 a seat a month; AI resolutions $0.09 to $0.20 each; voice from $0.10 a minute; 14-day free trial.",
				source: "https://nateq.io/pricing",
			},
			data: {
				value: "Publishes PDPL, UAE data-protection and GDPR notes. Hosting location is not stated publicly.",
				source: "https://nateq.io/llms.txt",
			},
			market: {
				value: "Saudi Arabia and the UAE, plus Kuwait, Qatar, Bahrain and Oman. NATEQ LLC (Wyoming, USA), founded 2025.",
				source: "https://nateq.io/about",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "arabybot",
		name: "ArabyBot",
		url: "https://arabybot.com",
		group: "gulf",
		summary:
			"A Cairo-based AI agent for WhatsApp, Instagram, Messenger and TikTok comments, built for small and mid-size Arab stores, with its own store builder.",
		bestFor:
			"Small shops and grocers that take orders on WhatsApp from a price sheet and want a low monthly price and a fast start.",
		description:
			"Hephon Commerce vs ArabyBot for grocers: WhatsApp list ordering, Arabic dialects, branch prices, voice and public pricing compared, with sources and dates.",
		inShort:
			"Choose ArabyBot if you are a small grocer taking WhatsApp orders from a price sheet and want a published price from $10 a month. Choose Hephon Commerce if you run several branches with their own prices, a large live catalog, and want shoppers to order inside your app and by phone too. Both confirm the order with the shopper before it is prepared.",
		chooseThem: [
			"Your catalog is a Google Sheet or a daily export.",
			"You want delivery zones, minimum orders and time slots handled in the chat.",
			"Instagram and TikTok comments matter to you.",
			"Budget is the deciding factor.",
		],
		chooseUs: [
			"You have many branches with their own prices and stock.",
			"Your app is a main channel, not only WhatsApp.",
			"Shoppers call to order.",
			"You need recipes, budgets and halal rules enforced on every channel.",
		],
		stronger: [
			"Public prices: bot plans from $10 to $50 a month, a store builder from $9, and a 7-day free trial.",
			"List ordering: a shopper sends a typed or photographed list, each line is matched to your catalog and priced, and unclear lines are asked about.",
			"Delivery zones, minimum orders, fixed time slots and weekly reorders are handled in the chat.",
			"More dialects named: Egyptian, Gulf, Levantine, Iraqi, Maghrebi and Modern Standard Arabic.",
			"Instagram, Messenger, Facebook and TikTok comments, and Gulf payment links including Tabby and Tamara.",
		],
		hephonStronger: [
			"Each branch's own prices and stock, from a live catalog.",
			"Inside your iOS, Android and web apps through an SDK.",
			"Phone and WhatsApp call ordering, with keypad confirmation.",
			"Recipes to basket with pack sizes.",
			"Content rules enforced by the server, and a test suite run on your catalog before changes go live.",
		],
		limits: [
			"Salla and Zid connectors are not open yet (its pages say they open by agreement).",
			"Per-branch prices and stock are not stated publicly; it routes conversations to a branch's team.",
			"Voice calling is not stated publicly; it understands voice notes.",
		],
		alongside:
			"Keep ArabyBot on social comments and run Hephon Commerce in your app or on the phone line, then compare orders branch by branch.",
		faq: [
			{
				q: "Can ArabyBot take grocery orders on WhatsApp?",
				a: "Yes. Its grocery page says a shopper can send a list, typed or photographed, and the bot matches each line to your catalog, prices it, offers your written substitutes and confirms with buttons before preparation.",
			},
			{
				q: "Where does each one read prices from?",
				a: "ArabyBot's grocery page reads a Google Sheet or a file exported daily from your system, and its store integrations read live stock. Hephon Commerce reads your live catalog with each branch's own prices and stock.",
			},
			{
				q: "Do they handle phone calls?",
				a: "ArabyBot understands and answers voice notes; voice calling is not stated publicly. Hephon Commerce takes phone and WhatsApp calls, builds the basket during the call and asks for keypad 1 to confirm.",
			},
			{
				q: "Which is cheaper?",
				a: "ArabyBot publishes prices from $10 a month. Hephon Commerce has no public price and gives each retailer a proposal.",
			},
		],
		facts: {
			channels: {
				value: "WhatsApp Cloud API, Instagram DMs and comments, Messenger, Facebook and TikTok comments, and website chat.",
				source: "https://arabybot.com/llms.txt",
			},
			languages: {
				value: `${NS} Arabic first.`,
				source: "https://arabybot.com/",
			},
			arabic: {
				value: "Egyptian, Gulf, Levantine, Iraqi, Maghrebi and Modern Standard Arabic; on WhatsApp it defaults to the dialect of the customer's dialling code.",
				source: "https://arabybot.com/",
			},
			arabizi: { value: NS, source: "https://arabybot.com/llms.txt" },
			voice: {
				value: "Understands and answers voice notes. Voice calling is not stated publicly.",
				source: "https://arabybot.com/compare/chatmaxima",
			},
			whatsapp: {
				value: "Takes the order in the chat: a typed or photographed list is matched line by line and priced, with cash on delivery confirmation and payment links.",
				source: "https://arabybot.com/industries/grocery",
			},
			catalog: {
				value: "Reads products, variants and stock at the moment of the question; for grocers, a Google Sheet or daily file. Per-branch prices are not stated publicly.",
				source: "https://arabybot.com/store",
			},
			recipes: {
				value: NS,
				source: "https://arabybot.com/industries/grocery",
			},
			checkout: {
				value: "Confirms with buttons before preparation, showing items, prices, address, time slot and payment method.",
				source: "https://arabybot.com/industries/grocery",
			},
			contentRules: {
				value: `${NS} It sells only what is in your catalog.`,
				source: "https://arabybot.com/industries/grocery",
			},
			handover: {
				value: "Assigns the thread to a person with an app alert, and hands off when the answer is not in your files.",
				source: "https://arabybot.com/guides/whatsapp-sla-escalation",
			},
			integrations: {
				value: "Shopify, WooCommerce, Easy Orders, Lightfunnels and custom stores by API and webhooks. Salla and Zid open by agreement.",
				source: "https://arabybot.com/integrations/salla",
			},
			pricing: {
				value: "Bot plans $10 to $50 a month; store plans $9 to $39; 7-day free trial. Meta fees billed to your own Meta account.",
				source: "https://arabybot.com/",
			},
			data: {
				value: "Verified Meta Business Partner. Hosting and data location are not stated publicly.",
				source: "https://arabybot.com/llms.txt",
			},
			market: {
				value: "22 Arab markets, strongest in Egypt, Saudi Arabia and the Gulf. Founded 2024, based in Cairo.",
				source: "https://arabybot.com/countries",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "askit",
		name: "AskIt",
		url: "https://askit.cfd",
		group: "gulf",
		summary:
			"An AI sales and service agent for retail chains in the UAE and Saudi Arabia that answers from a store's connected catalog, with in-store shelf directions.",
		bestFor:
			"Retail chains that want a website and in-store agent with shelf directions, and a presence inside ChatGPT.",
		description:
			"Hephon Commerce vs AskIt for Gulf retailers: channels, Arabic, branch stock, WhatsApp ordering, voice and in-store help compared, with a source for each row.",
		inShort:
			"Choose AskIt if you want a website and in-store agent that answers from your catalog in AED or SAR, gives shelf directions and appears inside ChatGPT. Choose Hephon Commerce if shoppers should build a grocery basket and confirm an order in Gulf Arabic or Arabizi, in your app, on WhatsApp or by phone. AskIt is careful about what it does not claim, and so should any vendor you pick.",
		chooseThem: [
			"Shoppers in your stores need to find the shelf.",
			"You want an agent on your website without an integration project.",
			"A ChatGPT app is part of your plan.",
		],
		chooseUs: [
			"The goal is orders, not only answers.",
			"Saudi and Gulf Arabic and Arabizi must work in chat and on calls.",
			"You want recipes, budgets and halal rules handled.",
		],
		stronger: [
			"Shelf directions in store, from verified location records, with QR codes in the aisle.",
			"A ChatGPT app channel.",
			"Learns your website's own search to see live prices without a custom integration.",
			"Plain public statements of what it does not claim: no dialect benchmark, no guaranteed uplift, no compliance certification.",
		],
		hephonStronger: [
			"Basket and checkout in the chat and on calls, confirmed by the shopper.",
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with an editable phrasebook.",
			"Recipes to basket with pack sizes.",
			"Content rules enforced by the server.",
			"A live inbox where your team can take over any chat.",
		],
		limits: [
			"Pricing is not stated publicly.",
			"Ordering and checkout are not stated publicly.",
			"It says it makes no dialect benchmark claim.",
		],
		alongside:
			"Use AskIt for shelf directions in your stores and Hephon Commerce for ordering in your app and on WhatsApp: they answer different questions.",
		faq: [
			{
				q: "Can AskIt take orders?",
				a: "The pages we checked describe answers from your catalog, prices and branch stock, and shelf directions, but not ordering or checkout. Hephon Commerce builds the basket and a checkout the shopper confirms.",
			},
			{
				q: "Which handles Gulf Arabic and Arabizi?",
				a: "AskIt lists English and Arabic and says it makes no dialect benchmark claim. Hephon Commerce understands Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, and answers in the script it was asked in.",
			},
			{
				q: "Do both use branch stock?",
				a: "Yes. AskIt answers from branch stock and warns that connected data can go stale. Hephon Commerce uses each branch's own prices and stock and offers only what the shopper's branch can deliver.",
			},
		],
		facts: {
			channels: {
				value: "Website widget, WhatsApp, voice calls, QR codes in store and a ChatGPT app.",
				source: "https://askit.cfd/llms.txt",
			},
			languages: {
				value: "English and Arabic.",
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			arabic: {
				value: `${NS} AskIt says it makes no dialect benchmark claim.`,
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			arabizi: {
				value: NS,
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			voice: {
				value: "Voice calls are a listed channel. Phone ordering is not stated publicly.",
				source: "https://askit.cfd/llms.txt",
			},
			whatsapp: {
				value: `${NS} WhatsApp is a listed channel.`,
				source: "https://askit.cfd/llms.txt",
			},
			catalog: {
				value: "Answers only from the business's catalog, prices (AED, SAR) and branch stock; learns the store's website search for live prices. Says connected data can go stale.",
				source: "https://askit.cfd/llms.txt",
			},
			recipes: {
				value: NS,
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			checkout: {
				value: NS,
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			contentRules: {
				value: `${NS} It says it never invents prices or discounts.`,
				source: "https://askit.cfd/llms.txt",
			},
			handover: { value: NS, source: "https://askit.cfd/llms.txt" },
			integrations: {
				value: "Shopify connects directly; other integrations are reviewed per business; developer API and ChatGPT app.",
				source: "https://askit.cfd/llms.txt",
			},
			pricing: { value: NS, source: "https://askit.cfd/llms.txt" },
			data: {
				value: "Conversations and records stored in AskIt Cloud. It claims no compliance certification.",
				source: "https://askit.cfd/retail-ai-ksa.md",
			},
			market: {
				value: "Retail in the UAE (Dubai, Abu Dhabi, Sharjah) and Saudi Arabia (Riyadh, Jeddah, Dammam).",
				source: "https://askit.cfd/llms.txt",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "thikaa",
		name: "Thikaa",
		url: "https://thikaa.com",
		group: "gulf",
		summary:
			"A Bahrain-based, Salla-native AI chatbot and shared inbox for WhatsApp, Instagram, TikTok and web chat, with an AI agent for WhatsApp and phone calls.",
		bestFor:
			"Salla stores and small Gulf retailers that want WhatsApp sales automation at a low public price.",
		description:
			"Hephon Commerce vs Thikaa for Gulf stores: Salla, WhatsApp orders, Arabic dialects and Arabizi, voice calls, branch prices and pricing, compared with sources.",
		inShort:
			"Choose Thikaa if your store runs on Salla and you want a self-serve WhatsApp and social chatbot from $9.99 a month. Choose Hephon Commerce if you are a supermarket or grocery chain with branches, an app and phone orders, and you need the assistant to build baskets with each branch's prices. Thikaa is the faster, cheaper start; Hephon Commerce goes deeper on grocery.",
		chooseThem: [
			"You sell on Salla.",
			"You want to set it up yourself today.",
			"Instagram, TikTok and Telegram matter to you.",
			"You need Kurdish on calls.",
		],
		chooseUs: [
			"You run several branches with their own prices and stock.",
			"Shoppers should order inside your app.",
			"Recipes, budgets and pack sizes matter.",
			"Halal rules must be enforced by the system, not the prompt.",
		],
		stronger: [
			"A native Salla app with one-click install, plus Shopify and WooCommerce.",
			"Public prices from $9.99 to $49.99 a month, with a 7-day free trial and no card.",
			"More channels in one inbox: WhatsApp, Instagram, Facebook, TikTok, Telegram, email and web chat.",
			"Five Arabic dialects named for voice notes, plus Arabizi and Kurdish calls.",
			"Thirteen payment gateways for in-chat payment links, including Tap, Moyasar, Tabby and Tamara.",
		],
		hephonStronger: [
			"Each branch's own prices and stock.",
			"An SDK for your iOS, Android and web apps.",
			"Recipes to basket with pack sizes.",
			"Phone orders read back and confirmed on the keypad.",
			"Search results checked against the shopper's message, and a test suite on your catalog.",
		],
		limits: [
			"Zid is not supported yet (its own words).",
			"Per-branch prices and stock are not stated publicly.",
			"Content rules such as halal are not stated publicly.",
		],
		alongside:
			"Keep Thikaa on social channels and try Hephon Commerce inside your app or on the phone line first.",
		faq: [
			{
				q: "Does Thikaa create orders?",
				a: "Yes. Its AI searches the catalog and creates orders with hosted payment links, and supports cash on delivery and bank transfer. Hephon Commerce builds the basket and a checkout the shopper confirms with a tap, a signed page or keypad 1.",
			},
			{
				q: "Which one works with Salla?",
				a: "Both. Thikaa has a native Salla app. Hephon Commerce connects to Salla through the Hephon app, and to Zid, with stock per branch.",
			},
			{
				q: "Do both understand Arabizi?",
				a: 'Yes. Thikaa lists Arabizi (Franco-Arabic). Hephon Commerce reads Arabizi such as "7aleeb" for milk and answers in the same script.',
			},
			{
				q: "How do prices compare?",
				a: "Thikaa publishes plans from $9.99 to $49.99 a month, plus per-minute voice prices. Hephon Commerce has no public price and sends each retailer a proposal.",
			},
		],
		facts: {
			channels: {
				value: "WhatsApp, Instagram, Facebook, TikTok, Telegram, email and a website chat widget, in one inbox and mobile app.",
				source: "https://thikaa.com/llms.txt",
			},
			languages: {
				value: "Arabic and English; WhatsApp calls in Arabic, English and Kurdish.",
				source: "https://thikaa.com/llms.txt",
			},
			arabic: {
				value: "Voice notes in Gulf, Egyptian, Levantine, Maghrebi and Iraqi Arabic; matches the customer's dialect.",
				source: "https://thikaa.com/llms.txt",
			},
			arabizi: {
				value: "Arabizi (Franco-Arabic) is listed.",
				source: "https://thikaa.com/llms.txt",
			},
			voice: {
				value: "AI agent answers WhatsApp calls ($0.06 a minute) and phone calls on a real number ($0.50 a minute), with live transfer. Phone ordering is not stated publicly.",
				source: "https://thikaa.com/llms.txt",
			},
			whatsapp: {
				value: "Searches the catalog and creates orders with hosted payment links; cash on delivery and bank transfer.",
				source: "https://thikaa.com/llms.txt",
			},
			catalog: {
				value: "Catalog search across channels. Per-branch prices and stock are not stated publicly.",
				source: "https://thikaa.com/llms.txt",
			},
			recipes: { value: NS, source: "https://thikaa.com/llms.txt" },
			checkout: {
				value: "Creates the order and a hosted payment link; 13 gateways including Tap, Moyasar, Tabby and Tamara.",
				source: "https://thikaa.com/llms.txt",
			},
			contentRules: { value: NS, source: "https://thikaa.com/llms.txt" },
			handover: {
				value: "Multi-agent inbox with auto-assignment and routing; live transfer on calls.",
				source: "https://thikaa.com/llms.txt",
			},
			integrations: {
				value: "Native Salla app, Shopify and WooCommerce. Zid is not supported yet.",
				source: "https://thikaa.com/llms.txt",
			},
			pricing: {
				value: "$9.99 to $49.99 a month by bot conversations; 7-day free trial; 17% off yearly.",
				source: "https://thikaa.com/pricing.php",
			},
			data: {
				value: "Cloud service with a stated 99.9% uptime SLA; says GDPR compliant. Data location is not stated publicly.",
				source: "https://thikaa.com/llms.txt",
			},
			market: {
				value: "Middle East and North Africa; headquartered in Bahrain, founded 2023.",
				source: "https://thikaa.com/llms.txt",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "superchat",
		name: "Superchat",
		url: "https://www.superchat.ai",
		group: "other",
		summary:
			"A UAE-built personal AI assistant for consumers that takes actions, including shopping across online stores, through an app and WhatsApp.",
		bestFor:
			"Shoppers who want one personal assistant to compare, order and track purchases across many stores.",
		description:
			"Hephon Commerce vs Superchat (Shop Anything): a shopper's personal assistant against a retailer's own shopping assistant in the UAE, with sources.",
		inShort:
			"Superchat is a personal assistant that shops for consumers across public online stores; Hephon Commerce is the assistant a retailer runs on its own catalog, branches and channels. Choose Superchat if you are a shopper who wants one assistant for errands, bills and purchases. Choose Hephon Commerce if you are a retailer that wants your customers to order from you, with your prices and rules.",
		chooseThem: [
			"You are a shopper, not a retailer.",
			"You want one assistant across many stores, plus bills, calendar and travel.",
		],
		chooseUs: [
			"You are a retailer and want orders in your own app, WhatsApp number and phone line.",
			"Your prices, branches, rules and brand must stay yours.",
			"Orders must land in your platform.",
		],
		stronger: [
			"Works for the shopper across many stores, not one retailer's catalog.",
			"Goes beyond shopping: calendar, inbox, bill payments and travel.",
			"A public consumer price: free app, Plus at $9.99 a month.",
			"Anyone can use it without a retailer integration.",
		],
		hephonStronger: [
			"Runs on the retailer's own catalog, branch prices, stock and rules.",
			"Orders go to the retailer's platform or team.",
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			"Halal content rules enforced by the server.",
			"A live inbox for the retailer's team.",
		],
		limits: [
			"A consumer product: retailers do not run it on their own catalog.",
			"Arabic is not stated publicly; the App Store lists English.",
			"Shop Anything was launched as a WhatsApp beta.",
		],
		alongside:
			"There is nothing to switch: Superchat works for shoppers, Hephon Commerce for your store. A shopper using a personal assistant still benefits from your own assistant's prices and stock.",
		faq: [
			{
				q: "Is this the German Superchat?",
				a: "No. This page compares superchat.ai, a UAE-built personal assistant (Super Chat AI Technology Limited, incorporated in the DIFC) with the Shop Anything feature. superchat.com is a different company: a German business messaging platform.",
			},
			{
				q: "Can a supermarket use Superchat as its shopping assistant?",
				a: "Superchat is sold to individuals, with an enterprise plan for teams. The pages we checked do not describe a way for a retailer to run it on its own catalog. Hephon Commerce is built for that.",
			},
			{
				q: "What is Shop Anything?",
				a: "A Superchat feature, announced in launch coverage, that finds, compares and buys products from public online stores through chat, first as a WhatsApp beta.",
			},
		],
		facts: {
			channels: {
				value: "An iOS app and WhatsApp.",
				source: "https://www.superchat.ai/",
			},
			languages: {
				value: "English is the only language in its App Store listing.",
				source: "https://apps.apple.com/us/app/id6746163922",
			},
			arabic: { value: NS, source: "https://www.superchat.ai/" },
			arabizi: { value: NS, source: "https://www.superchat.ai/" },
			voice: {
				value: "Voice Agent support in the app; requests can be spoken.",
				source: "https://apps.apple.com/us/app/id6746163922",
			},
			whatsapp: {
				value: "Shop Anything finds, compares and buys products from public online stores through chat, launched as a WhatsApp beta (launch coverage).",
				source: "https://www.zawya.com/en/press-release/companies-news/superchat-unveils-shop-anything-and-brings-the-uae-closer-to-living-daily-life-through-one-conversation-fby1ue8f",
			},
			catalog: {
				value: "Searches public online stores for the shopper. A connection to a retailer's own catalog, branches or stock is not stated publicly.",
				source: "https://www.zawya.com/en/press-release/companies-news/superchat-unveils-shop-anything-and-brings-the-uae-closer-to-living-daily-life-through-one-conversation-fby1ue8f",
			},
			recipes: { value: NS, source: "https://www.superchat.ai/" },
			checkout: {
				value: "Payment in one flow, per the launch release; how the shopper confirms is not stated publicly.",
				source: "https://www.zawya.com/en/press-release/companies-news/superchat-unveils-shop-anything-and-brings-the-uae-closer-to-living-daily-life-through-one-conversation-fby1ue8f",
			},
			contentRules: { value: NS, source: "https://www.superchat.ai/" },
			handover: {
				value: `${NS} A Human Assistant plan is sold to individuals.`,
				source: "https://www.superchat.ai/",
			},
			integrations: {
				value: "The user's calendar, inbox, payments and travel. Retailer integrations are not stated publicly.",
				source: "https://www.superchat.ai/",
			},
			pricing: {
				value: "Free app; Plus $9.99 a month; enterprise plan for teams on request.",
				source: "https://www.superchat.ai/",
			},
			data: {
				value: "Enterprise plan lists SSO, SCIM, audit logs and HIPAA; says it never sells user information.",
				source: "https://www.superchat.ai/",
			},
			market: {
				value: "Consumers in the UAE. Super Chat AI Technology Limited, incorporated in the DIFC.",
				source: "https://www.superchat.ai/",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "alhena",
		name: "Alhena",
		url: "https://alhena.ai",
		group: "global",
		summary:
			"An AI shopping assistant and support agent for ecommerce brands (formerly Gleen AI), with 200+ integrations and public prices.",
		bestFor:
			"Shopify, WooCommerce and Salesforce Commerce Cloud brands that want sales and support in one AI agent, with help-desk integrations and enterprise security.",
		description:
			"Hephon Commerce vs Alhena for grocery retailers: Arabic, WhatsApp, voice, branch prices, integrations, security and public pricing compared, with sources.",
		inShort:
			"Choose Alhena if you are an ecommerce brand on Shopify, WooCommerce or Salesforce Commerce Cloud and want sales and support in one agent, with public prices and SOC 2 Type 2. Choose Hephon Commerce if you are a Gulf supermarket or grocery chain and shoppers need Gulf Arabic and Arabizi, branch prices, WhatsApp checkout and phone orders. Alhena is broader and more proven with global brands; Hephon Commerce is narrower and built for grocery in the Gulf.",
		chooseThem: [
			"You are a direct-to-consumer brand with a support team on Zendesk, Gorgias or similar.",
			"You need SOC 2 Type 2 and single-tenant hosting options.",
			"You want public plans and a free tier.",
			"Returns, exchanges and order tracking are a big share of your tickets.",
		],
		chooseUs: [
			"You sell groceries from several branches.",
			"Saudi and Gulf Arabic and Arabizi are your shoppers' languages.",
			"Shoppers should confirm orders on WhatsApp or by phone.",
			"You want recipes and pack sizes handled.",
		],
		stronger: [
			"Public prices: a free tier, then $199 to $999 a month billed yearly, and custom Enterprise.",
			"200+ integrations, including Shopify, WooCommerce, Salesforce Commerce Cloud, Zendesk, Gorgias, Freshdesk, Intercom and Kustomer.",
			"SOC 2 Type 2 and GDPR, with single-tenant and dedicated hosting on AWS, Azure or Google Cloud.",
			"Support automation: order tracking, cancellations, returns and exchanges, with agent assist for your team.",
			"Named customer results from brands such as Tatcha, Victoria Beckham and Puffy, and 90+ languages.",
		],
		hephonStronger: [
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with an editable phrasebook.",
			"Each branch's own prices and stock.",
			"Recipes to basket with pack sizes.",
			"WhatsApp checkout confirmed with a tap, and phone orders confirmed on the keypad.",
			"Halal content rules enforced by the server.",
		],
		limits: [
			"Arabic and Arabizi are not named on the pages we checked (it states 90+ languages).",
			"Grocery, recipes and branch prices are not described.",
			"WhatsApp is a listed channel; ordering on it is not described.",
		],
		alongside:
			"Keep Alhena on your website and support desk, and pilot Hephon Commerce on WhatsApp or the phone line for Arabic-speaking shoppers.",
		faq: [
			{
				q: "Does Alhena speak Arabic?",
				a: "Alhena states 90+ languages for its support agent; the pages we checked do not name Arabic, dialects or Arabizi. Hephon Commerce is built for Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			},
			{
				q: "Which is better for a Shopify store?",
				a: "Both connect to Shopify. Alhena adds public plans, 200+ integrations and support automation for returns and exchanges. Hephon Commerce adds grocery features: branch prices, recipes with pack sizes, WhatsApp checkout and phone orders.",
			},
			{
				q: "Which is more secure?",
				a: "Alhena states SOC 2 Type 2 and GDPR compliance and offers single-tenant hosting. Hephon Commerce has no SOC or ISO certification; our servers are in the European Union, credentials are encrypted, and no conversation is used to train a model.",
			},
			{
				q: "How do the prices compare?",
				a: "Alhena publishes plans from a free tier to $999 a month billed yearly. Hephon Commerce has no public price and sends each retailer a proposal.",
			},
		],
		facts: {
			channels: {
				value: "Web chat, email, Instagram, Facebook, WhatsApp, SMS, help desks, and voice on phone and SIP lines.",
				source: "https://alhena.ai/llms.txt",
			},
			languages: {
				value: "90+ languages.",
				source: "https://alhena.ai/llms.txt",
			},
			arabic: { value: NS, source: "https://alhena.ai/llms.txt" },
			arabizi: { value: NS, source: "https://alhena.ai/llms.txt" },
			voice: {
				value: "Voice agents on phone and SIP lines. Phone ordering of a basket is not stated publicly.",
				source: "https://alhena.ai/llms.txt",
			},
			whatsapp: {
				value: `${NS} WhatsApp is a listed channel.`,
				source: "https://alhena.ai/llms.txt",
			},
			catalog: {
				value: "Product discovery, recommendations, comparisons and bundles from live catalog and inventory data. Per-branch prices and stock are not stated publicly.",
				source: "https://alhena.ai/llms.txt",
			},
			recipes: { value: NS, source: "https://alhena.ai/llms.txt" },
			checkout: {
				value: "Checkout and purchase guidance; order tracking, cancellations, returns and exchanges as actions.",
				source: "https://alhena.ai/llms.txt",
			},
			contentRules: { value: NS, source: "https://alhena.ai/llms.txt" },
			handover: {
				value: "Human handoff with complete context, and agent assist for your team.",
				source: "https://alhena.ai/llms.txt",
			},
			integrations: {
				value: "Shopify, WooCommerce, Salesforce Commerce Cloud, Zendesk, Gorgias, Freshdesk, Intercom, Gladly, Kustomer and more (200+).",
				source: "https://alhena.ai/llms.txt",
			},
			pricing: {
				value: "Free (25 conversations); Essentials $199, Growth $499, Scale $999 a month billed yearly, each with a 14-day trial; Enterprise custom.",
				source: "https://alhena.ai/pricing",
			},
			data: {
				value: "SOC 2 Type 2 and GDPR; single-tenant and dedicated hosting on AWS, Azure or Google Cloud.",
				source: "https://alhena.ai/llms.txt",
			},
			market: {
				value: "Ecommerce and consumer brands; named customers include Tatcha, Victoria Beckham, Dryrobe and Puffy.",
				source: "https://alhena.ai/llms.txt",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "rep-ai",
		name: "Rep AI",
		url: "https://www.hellorep.ai",
		group: "global",
		summary:
			"A Shopify-first AI sales and support agent that starts conversations from shopper behaviour and handles checkout in the chat.",
		bestFor:
			"Shopify brands that want an inexpensive, self-serve AI sales agent on their website.",
		description:
			"Hephon Commerce vs Rep AI for grocery retailers: Shopify, proactive selling, Arabic, WhatsApp, voice, branch prices and public pricing compared, with sources.",
		inShort:
			"Choose Rep AI if you are a Shopify brand that wants a low-cost, self-serve sales agent on your website from $12 a month, with proactive outreach when shoppers hesitate. Choose Hephon Commerce if you are a Gulf grocery retailer whose shoppers write in Gulf Arabic or Arabizi, order on WhatsApp or by phone, and buy from a branch. Rep AI is the quicker start for a single Shopify store.",
		chooseThem: [
			"You run one Shopify store and want to start today.",
			"Proactive outreach to hesitating shoppers is the main goal.",
			"Budget matters more than channels.",
		],
		chooseUs: [
			"You sell groceries from branches with their own prices.",
			"Your shoppers write in Gulf Arabic or Arabizi.",
			"WhatsApp and phone orders matter.",
		],
		stronger: [
			"Public prices from $12 a month and a 14-day free trial with no card.",
			"Proactive outreach driven by shopper behaviour such as hesitation and exit risk.",
			"Upsells, downsells, cart edits and checkout inside the chat, plus order tracking, cancellations and returns.",
			"A built-in help desk (Rep Inbox) and connections to Gorgias, Zendesk, Kustomer, Freshdesk and Richpanel.",
		],
		hephonStronger: [
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			"Each branch's own prices and stock.",
			"WhatsApp checkout and phone ordering with explicit confirmation.",
			"Recipes to basket with pack sizes.",
			"Halal content rules enforced by the server.",
		],
		limits: [
			"Shopify first; other platforms are listed as integrations.",
			"Its own pages disagree on languages (all languages in 2024, English and Spanish in a 2026 post).",
			"Voice is not stated publicly.",
		],
		alongside:
			"Keep Rep AI on your Shopify website and run Hephon Commerce on WhatsApp or by phone for shoppers who write or speak Arabic.",
		faq: [
			{
				q: "Does Rep AI support Arabic?",
				a: "Rep AI's own pages disagree: a 2024 update says every language is supported, while a 2026 blog post says English and Spanish, with more in development. Ask them for a current list. Hephon Commerce understands Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			},
			{
				q: "Do both work with Shopify?",
				a: "Yes. Rep AI is Shopify first. Hephon Commerce connects to Shopify and WooCommerce, product feeds, its REST API and custom connectors.",
			},
			{
				q: "Which is cheaper?",
				a: "Rep AI publishes plans from $12 a month by site sessions. Hephon Commerce has no public price and sends a proposal per retailer.",
			},
		],
		facts: {
			channels: {
				value: "Website; email, Instagram, Messenger and WhatsApp as add-ons at $0.75 per AI-resolved conversation.",
				source: "https://www.hellorep.ai/pricing",
			},
			languages: {
				value: "Its pages disagree: all languages (2024 update) and English and Spanish (2026 post).",
				source: "https://news.hellorep.ai/p/rep-ai-news-march-2024",
			},
			arabic: { value: NS, source: "https://www.hellorep.ai/" },
			arabizi: { value: NS, source: "https://www.hellorep.ai/" },
			voice: { value: NS, source: "https://www.hellorep.ai/pricing" },
			whatsapp: {
				value: `${NS} WhatsApp is an add-on AI channel.`,
				source: "https://www.hellorep.ai/pricing",
			},
			catalog: {
				value: "Conversational product discovery, recommendations and upsells; proactive outreach when shoppers hesitate. Per-branch prices and stock are not stated publicly.",
				source: "https://www.hellorep.ai/",
			},
			recipes: { value: NS, source: "https://www.hellorep.ai/" },
			checkout: {
				value: "Checkout inside the chat and cart edits; order tracking, cancellations and returns.",
				source: "https://www.hellorep.ai/",
			},
			contentRules: { value: NS, source: "https://www.hellorep.ai/" },
			handover: {
				value: "Routes complex issues to people; Rep Inbox help desk; Gorgias, Zendesk, Kustomer, Freshdesk and Richpanel.",
				source: "https://www.hellorep.ai/pricing",
			},
			integrations: {
				value: "Shopify first; WooCommerce, Salesforce Commerce, Klaviyo and an MCP server are listed.",
				source: "https://www.hellorep.ai/pricing",
			},
			pricing: {
				value: "By monthly site sessions: Sales $12, Support $49, Concierge $130 a month; 14-day free trial.",
				source: "https://www.hellorep.ai/pricing",
			},
			data: {
				value: "Says it is SOC 2 compliant.",
				source: "https://www.hellorep.ai/platform",
			},
			market: {
				value: "Shopify ecommerce brands.",
				source: "https://www.hellorep.ai/",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "zoovu",
		name: "Zoovu",
		url: "https://zoovu.com",
		group: "global",
		summary:
			"An AI product discovery platform: conversational search, guided selling, product quizzes and product data enrichment, mostly for considered purchases.",
		bestFor:
			"Retailers and manufacturers of considered products (appliances, electronics, tools) that need guided selling and cleaner product data.",
		description:
			"Hephon Commerce vs Zoovu for retailers: conversational search, guided selling, Arabic, WhatsApp, voice, branch prices and pricing compared, with sources.",
		inShort:
			"Choose Zoovu if you sell considered products such as appliances or electronics and need guided selling, product quizzes and cleaner product data on your website and kiosks. Choose Hephon Commerce if you sell groceries and shoppers should build a basket in Gulf Arabic or Arabizi, in your app, on WhatsApp or by phone. Zoovu helps a shopper choose one product; Hephon Commerce helps them fill a basket.",
		chooseThem: [
			"Your products need questions answered before purchase.",
			"Your product data needs cleaning and enriching.",
			"You want in-store kiosks.",
			"Your security review requires SOC 2 Type II.",
		],
		chooseUs: [
			"Your shoppers buy many items at once.",
			"Each branch has its own prices and stock.",
			"WhatsApp and phone ordering matter.",
		],
		stronger: [
			"Guided selling, product quizzes and conversational Q&A flows for complex products.",
			"Product data cleansing and enrichment across ecommerce platforms.",
			"Over 200 languages, with regional terminology learned.",
			"SOC 2 Type II certified and GDPR compliant, with WCAG and ADA accessibility.",
			"In-store kiosks as well as website and apps.",
		],
		hephonStronger: [
			"Built for grocery baskets: budgets, cheapest-first, recipes with pack sizes.",
			"Each branch's own prices and stock.",
			"WhatsApp checkout and phone ordering.",
			"Saudi and Gulf Arabic and Arabizi, with an editable phrasebook.",
		],
		limits: [
			"Grocery is not among its listed industries.",
			"WhatsApp and voice are not stated publicly.",
			"Pricing is quote-based.",
		],
		alongside:
			"Zoovu and Hephon Commerce can sit side by side: guided selling on your website for complex categories, Hephon Commerce for basket orders in your app and on WhatsApp.",
		faq: [
			{
				q: "Is Zoovu built for grocery?",
				a: "Its AI shopping assistant page lists industries such as home appliances, consumer electronics, sports, tools, beauty and fashion; grocery is not among them. Hephon Commerce is built for supermarkets, grocery delivery and quick commerce.",
			},
			{
				q: "Which supports Arabic?",
				a: "Zoovu states its assistant speaks over 200 languages; dialects and Arabizi are not stated publicly. Hephon Commerce understands Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			},
			{
				q: "How is each priced?",
				a: "Both are quote-based. Zoovu combines a product fee with a usage or experience fee; Hephon Commerce sends a proposal based on stores and channels.",
			},
		],
		facts: {
			channels: {
				value: "Website, mobile apps, chatbots and in-store kiosks.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			languages: {
				value: "Over 200 languages; learns regional terminology.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			arabic: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			arabizi: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			voice: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			whatsapp: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			catalog: {
				value: "Conversational and site search that answers product questions with citations, plus product data enrichment. Per-branch prices and stock are not stated publicly.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			recipes: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			checkout: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			contentRules: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			handover: {
				value: NS,
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			integrations: {
				value: "SAP CX AI Toolkit, plus CRM and CDP systems.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			pricing: {
				value: "Quote-based: a product fee plus a usage or experience fee.",
				source: "https://zoovu.com/pricing",
			},
			data: {
				value: "SOC 2 Type II certified and GDPR compliant.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
			market: {
				value: "Appliances, electronics, sports, tools, healthcare, beauty, fashion and manufacturing. Grocery is not listed.",
				source: "https://zoovu.com/ai-shopping-assistant",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "constructor",
		name: "Constructor",
		url: "https://constructor.com",
		group: "global",
		summary:
			"An AI ecommerce search and product discovery platform with an AI Shopping Agent, used by large retailers, with a grocery offering.",
		bestFor:
			"Large online grocers and retailers whose biggest lever is site search, browse and recommendations on web and app.",
		description:
			"Hephon Commerce vs Constructor for grocers: AI search, recipes to cart, store stock, Arabic, WhatsApp, voice and checkout compared, with a source per row.",
		inShort:
			"Choose Constructor if your biggest lever is search, browse and recommendations on your website and app, at large scale, with recipes and carts built from your inventory. Choose Hephon Commerce if your shoppers need to order in Gulf Arabic or Arabizi, on WhatsApp and by phone as well as in your app, with each branch's prices. Many grocers could keep Constructor for site search and add Hephon Commerce for conversation.",
		chooseThem: [
			"Site search revenue is your main target.",
			"You run large A/B tests and need ranking controls.",
			"You want search, browse, recommendations and an agent from one vendor.",
		],
		chooseUs: [
			"Gulf Arabic and Arabizi are your shoppers' languages.",
			"WhatsApp and phone orders matter.",
			"Halal rules must be enforced on every channel.",
		],
		stronger: [
			"Large-scale search, browse and recommendations, personalised per shopper, with merchandiser controls the agent cannot override.",
			"Recipes generated from what the grocer has in stock, with every ingredient added to cart from the recipe page.",
			"Shows availability at the shopper's local store or fulfilment centre, and suggests substitutes when stock is disrupted.",
			"Agentic checkout inside the conversation through a Stripe partnership.",
			"Large retail references, such as Target Australia, Sephora and REI.",
		],
		hephonStronger: [
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi, with an editable phrasebook.",
			"WhatsApp ordering and phone ordering with explicit confirmation.",
			"Pack sizes chosen to cover each recipe line for the least money.",
			"Halal content rules enforced by the server.",
			"A live inbox where your team can take over.",
		],
		limits: [
			"Languages, WhatsApp and voice are not stated publicly.",
			"Pricing is not stated publicly.",
			"Store-level pricing is not stated publicly (store-level availability is).",
		],
		alongside:
			"Keep Constructor as your site search and add Hephon Commerce for conversations in your app, on WhatsApp and by phone.",
		faq: [
			{
				q: "Does Constructor turn recipes into a cart?",
				a: 'Yes. Constructor describes its AI Shopping Agent generating recipes from items the grocer has in stock, with every ingredient addable to cart from the recipe page. Hephon Commerce does this from a sentence such as "kabsa for 10", choosing the pack that covers each amount for the least money.',
			},
			{
				q: "Can I use both?",
				a: "Yes. Constructor is search and discovery on your website and app; Hephon Commerce is a shopping conversation in your app, on WhatsApp and by phone. They read the same catalog.",
			},
			{
				q: "Which handles Arabic?",
				a: "Constructor's pages we checked do not state languages. Hephon Commerce understands Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			},
		],
		facts: {
			channels: {
				value: "Website and mobile app; email, SMS and notifications for discovery.",
				source: "https://constructor.com/solutions/grocery-ecommerce-search-and-product-discovery",
			},
			languages: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			arabic: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			arabizi: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			voice: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			whatsapp: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			catalog: {
				value: "Personalised search, browse and recommendations; availability at the shopper's local store or fulfilment centre; substitutes when stock is disrupted. Store-level pricing is not stated publicly.",
				source: "https://constructor.com/solutions/grocery-ecommerce-search-and-product-discovery",
			},
			recipes: {
				value: "Generates recipes from in-stock items; shoppers add every ingredient to cart from the recipe page.",
				source: "https://constructor.com/blog/constructor-ai-shopping-agent-use-cases",
			},
			checkout: {
				value: "Connects to your checkout; a Stripe partnership adds checkout inside the agent conversation.",
				source: "https://constructor.com/solutions/constructor-stripe-agentic-checkout",
			},
			contentRules: {
				value: "Merchandiser rules and compliance guardrails the agent cannot override. Halal or alcohol rules are not stated publicly.",
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			handover: {
				value: NS,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			integrations: {
				value: "Works with your ecommerce stack; best with Constructor's Search, Browse, Collections and Recommendations.",
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			pricing: {
				value: `${NS} Demo and pilot on request.`,
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			data: {
				value: "Says no personally identifiable information is passed to language models; aligns with GDPR and CCPA.",
				source: "https://constructor.com/solutions/ai-shopping-agent",
			},
			market: {
				value: "Large retailers, with grocery chains among its customers; named brands include Target Australia, Sephora and REI.",
				source: "https://constructor.com/solutions/grocery-ecommerce-search-and-product-discovery",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "bloomreach-clarity",
		name: "Bloomreach Clarity",
		url: "https://www.bloomreach.com/en/use-cases/conversational-shopping",
		group: "global",
		summary:
			"Bloomreach's conversational shopping agent (its documentation now calls it Conversational agent), built on Bloomreach search and Google Gemini models.",
		bestFor:
			"Retailers already on Bloomreach search or marketing that want a conversational layer on their website.",
		description:
			"Hephon Commerce vs Bloomreach Clarity: conversational shopping, Arabic, WhatsApp, voice, branch prices, guardrails and data handling compared, with sources.",
		inShort:
			"Choose Bloomreach Clarity if you already run Bloomreach and want a conversational shopping layer on your website that uses your product feed and guardrails. Choose Hephon Commerce if your shoppers are in the Gulf and should order in Gulf Arabic or Arabizi, on WhatsApp, by phone and in your app, from their branch. Clarity extends a large commerce platform; Hephon Commerce is a grocery assistant across channels.",
		chooseThem: [
			"You already use Bloomreach search or marketing.",
			"Your conversation lives on the website.",
			"You want an agent from a large, established vendor.",
		],
		chooseUs: [
			"WhatsApp and phone ordering matter.",
			"Each branch has its own prices and stock.",
			"Saudi and Gulf Arabic and Arabizi are your shoppers' languages.",
		],
		stronger: [
			"Part of a large commerce platform covering search, merchandising and marketing.",
			"Asks clarifying questions on broad searches and starts conversations on product and category pages for shoppers who never search.",
			"Input and response guardrails checked against your guidelines.",
			"A private Gemini instance; customer data is never shared with third parties to train models.",
		],
		hephonStronger: [
			"WhatsApp checkout and phone ordering, confirmed by the shopper.",
			"Each branch's own prices and stock.",
			"Saudi and Gulf Arabic, Lebanese Arabic and Arabizi.",
			"Recipes to basket with pack sizes.",
			"Halal content rules enforced by the server.",
		],
		limits: [
			"Its documentation lists the website (desktop and mobile web) as the channel.",
			"Languages and pricing are not stated publicly.",
			"Not a support tool: it directs support questions to your team.",
		],
		alongside:
			"Keep Clarity on your Bloomreach website and add Hephon Commerce in your app, on WhatsApp and by phone.",
		faq: [
			{
				q: "What is Bloomreach Clarity called now?",
				a: "Bloomreach announced Clarity in 2023. Its current documentation calls the product Conversational agent, an AI shopping assistant that answers questions, recommends products and guides shoppers to checkout.",
			},
			{
				q: "Which channels does each support?",
				a: "Bloomreach's documentation lists the web, on desktop and mobile. Hephon Commerce runs in your iOS, Android and web apps, on your WhatsApp number and on phone calls.",
			},
			{
				q: "Do either train models on shopper conversations?",
				a: "Bloomreach says it never shares customer data with third parties to train the underlying models and runs a private Gemini instance. Hephon Commerce sends every model request with data collection denied, so no conversation is used to train a model.",
			},
		],
		facts: {
			channels: {
				value: "Website, on desktop and mobile web, launched through weblayers.",
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			languages: {
				value: NS,
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			arabic: {
				value: NS,
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			arabizi: {
				value: NS,
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			voice: {
				value: NS,
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			whatsapp: {
				value: NS,
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			catalog: {
				value: "Uses your product feed to answer product questions; asks clarifying questions on broad searches. Per-branch prices and stock are not stated publicly.",
				source: "https://documentation.bloomreach.com/clarity/docs/how-clarity-uses-ai",
			},
			recipes: {
				value: NS,
				source: "https://www.bloomreach.com/en/use-cases/conversational-shopping",
			},
			checkout: {
				value: "Guides shoppers from browsing to checkout and reaches out when they hesitate at checkout.",
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			contentRules: {
				value: "Input and response guardrails checked against your guidelines. Halal or alcohol rules are not stated publicly.",
				source: "https://documentation.bloomreach.com/clarity/docs/how-clarity-uses-ai",
			},
			handover: {
				value: "Not a support tool; directs support questions to your support team.",
				source: "https://documentation.bloomreach.com/clarity/docs/get-started-with-bloomreach-clarity",
			},
			integrations: {
				value: `${NS} Part of the Bloomreach platform.`,
				source: "https://www.bloomreach.com/en/use-cases/conversational-shopping",
			},
			pricing: {
				value: NS,
				source: "https://www.bloomreach.com/en/use-cases/conversational-shopping",
			},
			data: {
				value: "Private instance for Gemini calls; customer data never shared with third parties to train models. Hosting region is not stated publicly.",
				source: "https://documentation.bloomreach.com/clarity/docs/how-clarity-uses-ai",
			},
			market: {
				value: "Ecommerce retailers; named example: TFG's Bash platform (South Africa, fashion and lifestyle).",
				source: "https://www.bloomreach.com/en/use-cases/conversational-shopping",
			},
		},
		checked: CHECKED,
	},
	{
		slug: "build-vs-buy",
		name: "Custom build",
		vsName: "a custom build",
		url: "https://learn.microsoft.com/en-us/azure/ai-foundry/responsible-ai/openai/data-privacy",
		group: "other",
		summary:
			"Building your own assistant on Azure OpenAI or OpenAI, as Grandiose did with GrandChef, built with Microsoft on Azure AI services.",
		bestFor:
			"Retailers with an in-house AI and engineering team, one platform to connect, and a reason to own every part of the assistant.",
		description:
			"Hephon Commerce vs a custom build on Azure OpenAI or OpenAI for grocery retailers: what you build, what you own, and how long it takes, with sources.",
		inShort:
			"Build your own if you have an AI and engineering team, want to own every part of the assistant, and can keep it tested as models change, as Grandiose did with GrandChef. Choose Hephon Commerce if you want Gulf Arabic, branch prices, recipes with pack sizes, WhatsApp checkout and phone orders already built and tested on your catalog. Some retailers do both: buy first, then build what is unique to them.",
		chooseThem: [
			"You have an in-house AI team and a long roadmap for it.",
			"Your contracts already run on Azure or OpenAI.",
			"The assistant must use loyalty or diet profiles only you hold.",
			"Owning the code matters more than time to launch.",
		],
		chooseUs: [
			"You want to launch on one channel in weeks, not build a team first.",
			"You need app, WhatsApp and phone ordering in one basket.",
			"You want dialect, search checks and confirmation rules already tested.",
		],
		stronger: [
			"Full ownership of the code, the prompts and the roadmap.",
			"Data stays in the Azure geography you choose, and prompts and completions are not used to train models.",
			"Tailored to your own data: GrandChef tailors carts to diet and allergies from the shopper's account.",
			"Launch press: GrandChef was covered by Arabian Business and tbreak.",
			"No dependence on a smaller vendor.",
		],
		hephonStronger: [
			"Already built: Gulf dialect phrasebook, search checked against the shopper's message, pack sizes, branch prices, server-side content rules, the call service and confirmation steps.",
			"A test suite generated from your catalog runs before every change to the prompt, model or search.",
			"App, WhatsApp and phone ordering on one basket.",
			"Maintained as models change.",
		],
		limits: [
			"Your team builds and maintains every part: search, dialects, channels, checkout and tests.",
			"Model usage is billed by your cloud provider, on top of the team's time.",
			"Azure Global and Data Zone deployments may process prompts outside your chosen geography.",
		],
		alongside:
			"Start with Hephon Commerce on one channel and keep your team on the parts only you can build. Our REST API and webhooks let your own systems read every order.",
		faq: [
			{
				q: "What is GrandChef?",
				a: "An AI assistant in the Grandiose app (UAE) that recommends recipes and adds all the ingredients to the cart, tailored to diet or allergies. Launch coverage in September 2025 says it is built with Microsoft, with its backend on Azure AI services.",
			},
			{
				q: "Are prompts used to train models on Azure OpenAI?",
				a: "Microsoft says prompts and completions are not used to train or improve the base models, and that stored data stays in your chosen Azure geography. Global and Data Zone deployments may process prompts in other regions.",
			},
			{
				q: "What does a custom build need besides the model?",
				a: "Catalog search that works in Arabic script and Arabizi, branch price and stock sync, dialect replies, WhatsApp and phone channels, a confirmation step before any order, content rules, an inbox for your team, and tests that run before every change.",
			},
			{
				q: "Can we buy first and build later?",
				a: "Yes. Hephon Commerce sends orders to your platform through its API and has a REST API with signed webhooks, so your team can build on top of it or replace parts later.",
			},
		],
		facts: {
			channels: {
				value: "Whatever you build. GrandChef runs inside the Grandiose iOS and Android apps.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			languages: {
				value: "Depends on the model you pick and how you test it. GrandChef's languages are not stated publicly.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			arabic: {
				value: "Your team builds and tests dialect handling and replies.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			arabizi: {
				value: "Your team builds and tests it, including search.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			voice: {
				value: "You add speech recognition, speech synthesis and telephony. Voice is not mentioned in GrandChef's launch coverage.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			whatsapp: {
				value: "You build the WhatsApp Business Platform integration, messages and checkout.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			catalog: {
				value: "You build search, ranking, and branch price and stock sync. GrandChef matches recipes to what is in store.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			recipes: {
				value: "Possible: GrandChef adds all the ingredients of a recipe to the cart, tailored to diet or allergies.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			checkout: {
				value: "You design the confirmation step and connect it to your order system.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			contentRules: {
				value: "Azure guardrails filter harmful content; rules for products your store does not sell are yours to build.",
				source: "https://learn.microsoft.com/en-us/azure/ai-foundry/responsible-ai/openai/data-privacy",
			},
			handover: {
				value: "You build an inbox or connect your help desk.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			integrations: {
				value: "Anything you build. GrandChef is built with Microsoft, its backend on Azure AI services.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
			pricing: {
				value: "Model usage billed by your cloud provider, plus your team's time to build and maintain it.",
				source: "https://learn.microsoft.com/en-us/azure/ai-foundry/responsible-ai/openai/data-privacy",
			},
			data: {
				value: "On Azure, prompts and completions are not used to train models, and stored data stays in your chosen geography; Global and Data Zone deployments may process elsewhere.",
				source: "https://learn.microsoft.com/en-us/azure/ai-foundry/responsible-ai/openai/data-privacy",
			},
			market: {
				value: "Yours. GrandChef: UAE only at launch, free with a Grandiose account.",
				source: "https://tbreak.com/grandiose-grandchef-ai-shopping-assistant/",
			},
		},
		checked: CHECKED,
	},
];

/** Every comparison page's slug, for the sitemap and llms.txt. */
export const COMPARE_SLUGS = VENDORS.map((vendor) => vendor.slug);

export function vendorBySlug(slug: string) {
	return VENDORS.find((vendor) => vendor.slug === slug);
}

/** "Hephon Commerce vs X" with the vendor's name as it reads after "vs". */
export function vsTitle(vendor: Vendor) {
	return `Hephon Commerce vs ${vendor.vsName ?? vendor.name}`;
}
